/**
 * Hotjar Survey Detection Utility
 * Detects when Hotjar surveys are shown/hidden using MutationObserver
 */

(function () {
  'use strict';

  if (typeof window.theme === 'undefined') {
    window.theme = {};
  }

  window.theme.hotjarSurveyDetection = {
    /**
     * Initialize Hotjar survey detection
     * @param {Function} onShow - Callback when survey becomes visible (receives survey element)
     * @param {Function} onHide - Callback when survey is hidden
     * @returns {MutationObserver} Observer instance for cleanup
     */
    init: function (onShow, onHide) {
      // Common Hotjar survey selectors
      const selectors = [
        '._hj-widget-container',
      ];

      let currentlyVisible = false;
      let currentSurveyElement = null;

      /**
       * Check if any Hotjar survey is currently visible
       * @returns {Element|null} The visible survey element or null
       */
      const findVisibleSurvey = () => {
        for (const selector of selectors) {
          const elements = document.querySelectorAll(selector);
          for (const element of elements) {
            // Check if element is visible (not display:none or hidden)
            if (element.offsetParent !== null ||
              (element.offsetWidth > 0 && element.offsetHeight > 0)) {
              return element;
            }
          }
        }
        return null;
      };

      /**
       * Check visibility and trigger callbacks
       */
      const checkVisibility = () => {
        const survey = findVisibleSurvey();
        const isVisible = !!survey;

        // Survey became visible
        if (isVisible && !currentlyVisible) {
          currentlyVisible = true;
          currentSurveyElement = survey;
          if (onShow && typeof onShow === 'function') {
            onShow(survey);
          }
        }
        // Survey was hidden
        else if (!isVisible && currentlyVisible) {
          currentlyVisible = false;
          if (onHide && typeof onHide === 'function') {
            onHide(currentSurveyElement);
          }
          currentSurveyElement = null;
        }
      };

      // Create MutationObserver to watch for DOM changes
      const observer = new MutationObserver((mutations) => {
        // Use requestAnimationFrame to debounce rapid mutations
        window.requestAnimationFrame(checkVisibility);
      });

      // Start observing the document body for changes
      observer.observe(document.body, {
        childList: true,      // Watch for added/removed nodes
        subtree: true,        // Watch all descendants
        attributes: true,     // Watch for attribute changes
        attributeFilter: ['style', 'class', 'id'] // Only watch relevant attributes
      });

      // Perform initial check
      checkVisibility();

      return observer;
    },

    /**
     * Simple check if Hotjar survey is currently visible
     * @returns {Boolean} True if survey is visible
     */
    isVisible: function () {
      const element = document.querySelector('._hj-widget-container');
      return element && (element.offsetParent !== null ||
        (element.offsetWidth > 0 && element.offsetHeight > 0));
    },

    /**
     * Get the current visible survey element
     * @returns {Element|null} The visible survey element or null
     */
    getSurveyElement: function () {
      const element = document.querySelector('._hj-widget-container');
      if (element && (element.offsetParent !== null ||
        (element.offsetWidth > 0 && element.offsetHeight > 0))) {
        return element;
      }
      return null;
    }
  };

  // Auto-initialize to add 'hotjar-loaded' class
  document.addEventListener('DOMContentLoaded', function () {
    window.theme.hotjarSurveyDetection.init(
      function (survey) {
        // Add class when survey is shown
        document.body.classList.add('hotjar-loaded');

        // Log in development mode
        if (window.Shopify && window.Shopify.designMode) {
          console.log('[Hotjar] Survey shown:', survey);
        }
      },
      function () {
        // Remove class when survey is hidden
        document.body.classList.remove('hotjar-loaded');

        // Log in development mode
        if (window.Shopify && window.Shopify.designMode) {
          console.log('[Hotjar] Survey hidden');
        }
      }
    );
  });

})();
